import Swal from 'sweetalert2'
import {DropdownPattern} from '../../../components/Dropdown'
import {copyToPayment} from '../../../utils/copyToPayment'
import {
  generateSalePaymentLink,
  resendContractToClient,
  sendLinkChargeToCustomer,
  updateSaleReviewAction,
} from './core/_requests'
import {CancelSaleModal} from '../payment/components/cancelSaleModal'
import {ChangePaymentModal} from './changePaymentModal/ChangePaymentModal'
import {useState} from 'react'
import {phoneMask} from '../payment/utils/mask'
import {PageOverlayer} from '../../../components/PageOverlayer'
import {ISaleResponse} from '../../../coreGlobal/models/sale'

interface IDropdownSale {
  saleData: ISaleResponse
  canCanSale?: boolean
  canChangePayment?: boolean
}
const AUTOMATIC_PAYMENTS = [1, 2, 3, 4, 5]

export const DropdownSale = ({
  saleData: {id, client, billings, statusId, canReview},
  canCanSale = false,
  canChangePayment = false,
}: IDropdownSale) => {
  const canResendContract = statusId === 2 || statusId === 5
  const hasAutomaticPayment =
    billings?.some((billing) => AUTOMATIC_PAYMENTS.includes(billing.paymentOption?.id)) || false
  const isPendingSale = statusId === 1
  const reviewSales = statusId === 22 && canReview === true

  const hasPendingActions = isPendingSale && (hasAutomaticPayment || canCanSale || canChangePayment)
  const hasAnyAction = hasPendingActions || canResendContract || reviewSales

  const [isOpenCancelModal, setIsOpenCancelModal] = useState(false)
  const [isOpenChangePaymentModal, setIsOpenChangePaymentModal] = useState(false)
  const [loading, setLoading] = useState(false)

  const copyLink = () => copyToPayment(() => generateSalePaymentLink(id!))

  const resendLink = () => {
    Swal.fire({
      title: 'Deseja reenviar o link de pagamento?',
      html: `<b>Email:</b> ${client?.email}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
        cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true)
        try {
          Swal.fire({
            position: 'center',
            icon: 'info',
            title: 'Reenviando o Link de pagamento',
            showConfirmButton: false,
            allowOutsideClick: false,
            customClass: {
              confirmButton: 'btn btn-danger',
              cancelButton: 'btn btn-secondary',
            },
          })
          await sendLinkChargeToCustomer(id)
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'O Link de pagamento reenviando',
            showConfirmButton: true,
            allowOutsideClick: false,
            customClass: {
              confirmButton: 'btn btn-danger',
              cancelButton: 'btn btn-secondary',
            },
          })
          setLoading(false)
        } catch (error: any) {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: error.response.data.message,
            showConfirmButton: true,
            allowOutsideClick: false,
            customClass: {
              confirmButton: 'btn btn-danger',
              cancelButton: 'btn btn-secondary',
            },
          })
          setLoading(false)
        }
      }
    })
  }

  const cancelSale = () => setIsOpenCancelModal(true)

  const changePayment = () => setIsOpenChangePaymentModal(true)

  const resendContract = () => {
    Swal.fire({
      title: 'Como deseja reenviar o contrato?',
      html: `<b>Email:</b> ${client?.email}<br><b>Telefone:</b> ${phoneMask(
        client?.contacts[0].phone
      )}`,
      icon: 'warning',
      showCancelButton: true,
      showDenyButton: client?.contacts[0]?.countryId === 76,
      confirmButtonText: 'E-mail',
      denyButtonText: 'WhatsApp',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-primary',
        denyButton: 'btn btn-success',
        cancelButton: 'btn btn-secondary',
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Enviar por email
        setLoading(true)
        try {
          await resendContractToClient(id, 'email')
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Contrato reenviado por e-mail para o cliente.',
            showConfirmButton: true,
            allowOutsideClick: false,
            customClass: {
              confirmButton: 'btn btn-danger',
              cancelButton: 'btn btn-secondary',
            },
          })
        } catch (error: any) {
          await Swal.fire({
            position: 'center',
            icon: 'error',
            title: error.response.data.message,
            showConfirmButton: true,
            allowOutsideClick: false,
            customClass: {
              confirmButton: 'btn btn-danger',
              cancelButton: 'btn btn-secondary',
            },
          })
        } finally {
          setLoading(false)
        }
      } else if (result.isDenied) {
        // Enviar por WhatsApp
        setLoading(true)
        try {
          await resendContractToClient(id, 'whatsapp')
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Contrato reenviado por WhatsApp para o cliente.',
            showConfirmButton: true,
            allowOutsideClick: false,
            customClass: {
              confirmButton: 'btn btn-danger',
              cancelButton: 'btn btn-secondary',
            },
          })
        } catch (error: any) {
          await Swal.fire({
            position: 'center',
            icon: 'error',
            title: error.response.data.message,
            showConfirmButton: true,
            allowOutsideClick: false,
            customClass: {
              confirmButton: 'btn btn-danger',
              cancelButton: 'btn btn-secondary',
            },
          })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  const saleReviewAction = (saleReviewAction: boolean) => {
    Swal.fire({
      title: `Deseja ${saleReviewAction ? 'aceitar' : 'recusar'} essa venda?`,
      icon: 'warning',
      input: 'textarea',
      inputLabel: 'Motivo',
      inputPlaceholder: 'Informe o motivo...',
      showCancelButton: true,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary',
      },
      inputValidator: (value) => {
        if (!value || value.trim().length < 10) {
          return 'O motivo deve possuir no mínimo 10 caracteres.'
        }

        return undefined
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        const observation = result.value
        // Enviar por email
        setLoading(true)
        try {
          await updateSaleReviewAction(id, saleReviewAction, observation)
          await Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Venda atualizada com sucesso.',
            showConfirmButton: true,
            allowOutsideClick: false,
            customClass: {
              confirmButton: 'btn btn-danger',
              cancelButton: 'btn btn-secondary',
            },
          })
          window.location.href = `/sales/list/${id}`
        } catch (error: any) {
          await Swal.fire({
            position: 'center',
            icon: 'error',
            title: error?.response?.data?.message,
            showConfirmButton: true,
            allowOutsideClick: false,
            customClass: {
              confirmButton: 'btn btn-danger',
              cancelButton: 'btn btn-secondary',
            },
          })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  return (
    <>
      {loading && <PageOverlayer />}
      {!loading && (
        <>
          {hasAnyAction && (
            <DropdownPattern.Root>
              <DropdownPattern.List>
                {isPendingSale && (
                  <>
                    {hasAutomaticPayment && (
                      <>
                        <DropdownPattern.Action
                          index={0}
                          label='Copiar Link'
                          action={() => copyLink()}
                          key='action0'
                        />

                        <DropdownPattern.Action
                          index={1}
                          label='Reenviar Link'
                          action={() => resendLink()}
                          key='action1'
                        />
                      </>
                    )}
                    {canCanSale && (
                      <DropdownPattern.Action
                        index={2}
                        label='Cancelar Venda'
                        action={() => cancelSale()}
                        key='action2'
                      />
                    )}
                    {canChangePayment && (
                      <DropdownPattern.Action
                        index={3}
                        label='Alterar pagamento'
                        action={() => changePayment()}
                        key='action3'
                      />
                    )}
                  </>
                )}
                {canResendContract && (
                  <DropdownPattern.Action
                    index={4}
                    label='Reenviar Contrato'
                    action={() => resendContract()}
                    key='action4'
                  />
                )}
                {reviewSales && (
                  <>
                    <DropdownPattern.Action
                      index={5}
                      label='Aceitar Venda'
                      action={() => saleReviewAction(true)}
                      key='action5'
                    />

                    <DropdownPattern.Action
                      index={6}
                      label='Recusar Venda'
                      action={() => saleReviewAction(false)}
                      key='action6'
                    />
                  </>
                )}
              </DropdownPattern.List>
            </DropdownPattern.Root>
          )}

          {isOpenCancelModal && (
            <CancelSaleModal close={() => setIsOpenCancelModal(false)} saleId={Number(id)} />
          )}
          {isOpenChangePaymentModal && (
            <ChangePaymentModal
              id={id}
              billings={billings?.filter((item) => item.status === 'Pendente')}
              close={() => setIsOpenChangePaymentModal(false)}
            />
          )}
        </>
      )}
    </>
  )
}
